import { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { useLang } from '../../../context/LangContext';
import { fadeInUp } from '../../../animations/variants';

export const ChatDemo = () => {
  const { t } = useLang();
  const [visibleMessages, setVisibleMessages] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  const messages = [
    { type: 'client', key: 'chat.msg1' },
    { type: 'entity', key: 'chat.msg2' },
    { type: 'client', key: 'chat.msg3' },
    { type: 'entity', key: 'chat.msg4' },
    { type: 'system', key: 'chat.resolved' },
  ];

  useEffect(() => {
    if (!inView) return;
    const delays = [800, 1600, 2400, 3200, 4000];
    const timers = delays.map((delay, i) =>
      setTimeout(() => setVisibleMessages(i + 1), delay)
    );
    return () => timers.forEach(clearTimeout);
  }, [inView]);

  return (
    <section data-testid="chat-demo-section" className="relative py-24 md:py-32 bg-[#020209]">
      {/* Orb */}
      <motion.div
        className="absolute rounded-full blur-[100px] opacity-15 bg-brand-green w-96 h-96 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div {...fadeInUp} className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
            {t("chat.title")}
          </h2>
        </motion.div>

        <div ref={ref} className="max-w-lg mx-auto">
          <motion.div
            className="glass-card overflow-hidden"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 55, damping: 18 }}
          >
            {/* Header */}
            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/[0.06]">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-sm text-neutral-300 font-medium">{t("chat.status")}</span>
            </div>

            {/* Messages */}
            <div className="p-5 space-y-3 min-h-[280px]">
              {messages.map((msg, i) => {
                if (i >= visibleMessages) return null;

                if (msg.type === 'system') {
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4 }}
                      className="flex items-center justify-center gap-2 py-2"
                    >
                      <CheckCircle2 size={16} className="text-emerald-400" />
                      <span className="text-xs text-emerald-400 font-medium">{t(msg.key)}</span>
                    </motion.div>
                  );
                }

                const isClient = msg.type === 'client';
                return (
                  <motion.div
                    key={i}
                    className={`flex ${isClient ? 'justify-end' : 'justify-start'}`}
                    initial={{ opacity: 0, x: isClient ? 20 : -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <div
                      className={`max-w-[80%] px-4 py-2.5 text-sm rounded-2xl ${
                        isClient
                          ? 'bg-brand-green/20 border border-brand-green/20 text-neutral-200 rounded-tr-md'
                          : 'bg-white/[0.05] border border-white/[0.08] text-neutral-300 rounded-tl-md'
                      }`}
                    >
                      {t(msg.key)}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
